/**
 * shiftRequestsCloud.js — hour-change / add-shift / delete-shift requests (shiftRequests).
 *
 * One pending request per thing: sending the same request again (a double tap, a second
 * device, or "I'll just send it again") updates the pending one instead of adding a
 * duplicate for the approver.
 *   edit, delete-shift — one pending per shift
 *   new-shift          — one pending per person per overlapping time window
 * The lookups are equality-only, so they need no composite index.
 */
import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { toDate } from './timesheetUtils';

const requestsCol = () => collection(db, 'shiftRequests');

/** The pending request this one would duplicate, or null. */
export async function findPendingTwin({ type = 'edit', shiftId, userId, requestedClockIn, requestedClockOut }) {
  const q = shiftId
    ? query(requestsCol(), where('shiftId', '==', shiftId), where('status', '==', 'pending'))
    : query(requestsCol(), where('userId', '==', userId), where('status', '==', 'pending'));
  const snap = await getDocs(q);
  const list = [];
  snap.forEach(d => list.push({ id: d.id, ...d.data() }));
  return list.find(r => {
    if ((r.type || 'edit') !== type) return false;
    if (shiftId) return true;
    const a0 = toDate(requestedClockIn), a1 = toDate(requestedClockOut);
    const b0 = toDate(r.requestedClockIn), b1 = toDate(r.requestedClockOut);
    return !!(a0 && a1 && b0 && b1 && a0 < b1 && b0 < a1);
  }) || null;
}

/**
 * Send a request — or update the matching pending one. Resolves { id, updated }.
 * Rejects with code 'already-pending' when a twin exists but this user may not edit it
 * (e.g. someone else sent it), so the caller can say so instead of adding a duplicate.
 */
export async function submitShiftRequest(data) {
  const type = data.type || 'edit';
  const twin = await findPendingTwin({ ...data, type });
  if (twin) {
    const changes = { reason: data.reason, updatedAt: Timestamp.now(), resubmits: (twin.resubmits || 0) + 1 };
    if (data.requestedClockIn) changes.requestedClockIn = data.requestedClockIn;
    if (data.requestedClockOut) changes.requestedClockOut = data.requestedClockOut;
    if (data.totalMinutes != null) changes.totalMinutes = data.totalMinutes;
    try {
      await updateDoc(doc(db, 'shiftRequests', twin.id), changes);
    } catch (err) {
      if (err?.code === 'permission-denied') {
        throw Object.assign(new Error('A request for this is already waiting for approval.'), { code: 'already-pending', twin });
      }
      throw err;
    }
    return { id: twin.id, updated: true };
  }
  const ref = await addDoc(requestsCol(), {
    ...data, type, status: 'pending', createdAt: Timestamp.now(), reviewedBy: null, reviewedAt: null,
  });
  return { id: ref.id, updated: false };
}
